import React, { useRef, useEffect, useState } from 'react';
import './GraphVisualizer.css';

/**
 * Graph Visualization Component using Canvas
 * 
 * Provides graph visualization similar to the lab model with:
 * - Interactive node selection
 * - Path highlighting
 * - Step-by-step algorithm visualization
 * - Professional styling and dynamic scaling
 */
const GraphVisualizer = ({ 
  nodes = [], 
  edges = [], 
  selectedPath = [],
  onNodeClick,
  width = 600,
  height = 400,
  stepData = null,
  customPath = [],
  optimalPath = [],
  showOptimalPath = true
}) => {
  const canvasRef = useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  //Drag Node States
  const [draggedNode, setDraggedNode] = useState(null);

  // Node radius and colors (lab-style)
  const NODE_RADIUS = 35; // Increased from 25 to 35 (1.4x larger)
  const COLORS = {
    node: '#ffffff',
    nodeHover: '#d2e5ff',
    nodeSelected: '#ffd700',
    nodeCurrent: '#f56565',
    nodeVisited: '#48bb78',
    edge: '#848484',
    edgePath: '#44b700',
    edgeCustomPath: '#ff6b35', // Orange for custom path
    edgeOptimalPath: '#4ecdc4', // Teal for optimal path
    text: '#333333',
    border: '#666666'
  };

  // Calculate scale and offset to fit graph in canvas
  const calculateFitTransform = () => {
    if (nodes.length === 0) return { scale: 1, offsetX: 0, offsetY: 0 };
    
    const padding = 60; // Reduced padding to allow more space for graph
    const minX = Math.min(...nodes.map(n => n.x));
    const maxX = Math.max(...nodes.map(n => n.x));
    const minY = Math.min(...nodes.map(n => n.y));
    const maxY = Math.max(...nodes.map(n => n.y));
    
    const graphWidth = maxX - minX;
    const graphHeight = maxY - minY;
    
    const scaleX = (width - 2 * padding) / graphWidth;
    const scaleY = (height - 2 * padding) / graphHeight;
    const scale = Math.min(scaleX, scaleY, 1.5); // Allow scaling up to 1.5x
    
    const offsetX = (width - graphWidth * scale) / 2 - minX * scale;
    const offsetY = (height - graphHeight * scale) / 2 - minY * scale;
    
    return { scale, offsetX, offsetY };
  };

  // Transform coordinates
  const transformCoord = (x, y, transform) => {
    return {
      x: x * transform.scale + transform.offsetX,
      y: y * transform.scale + transform.offsetY
    };
  };

  // Find node at position
  const findNodeAtPosition = (x, y) => {
    const transform = calculateFitTransform();
    return nodes.find(node => {
      const pos = transformCoord(node.x, node.y, transform);
      const scaledRadius = NODE_RADIUS * transform.scale;
      const dx = pos.x - x;
      const dy = pos.y - y;
      return Math.sqrt(dx * dx + dy * dy) <= scaledRadius;
    });
  };

  // Function to determine edge type and styling
  const getEdgeStyle = (fromNodeId, toNodeId) => {
    // Check if edge is in custom path
    const customPathIndex1 = customPath.indexOf(fromNodeId);
    const customPathIndex2 = customPath.indexOf(toNodeId);
    const isInCustomPath = customPathIndex1 !== -1 && customPathIndex2 !== -1;
    const isCustomConsecutive = isInCustomPath && Math.abs(customPathIndex1 - customPathIndex2) === 1;

    // Check if edge is in optimal path
    const optimalPathIndex1 = optimalPath.indexOf(fromNodeId);
    const optimalPathIndex2 = optimalPath.indexOf(toNodeId);
    const isInOptimalPath = optimalPathIndex1 !== -1 && optimalPathIndex2 !== -1;
    const isOptimalConsecutive = isInOptimalPath && Math.abs(optimalPathIndex1 - optimalPathIndex2) === 1;

    // Check if edge is in original selected path (algorithm mode)
    const selectedPathIndex1 = selectedPath.indexOf(fromNodeId);
    const selectedPathIndex2 = selectedPath.indexOf(toNodeId);
    const isInSelectedPath = selectedPathIndex1 !== -1 && selectedPathIndex2 !== -1;
    const isSelectedConsecutive = isInSelectedPath && Math.abs(selectedPathIndex1 - selectedPathIndex2) === 1;

    // Priority: Custom path > Optimal path > Selected path > Default
    if (isInCustomPath && isCustomConsecutive) {
      return { color: COLORS.edgeCustomPath, width: 5, priority: 3 };
    } else if (isInOptimalPath && isOptimalConsecutive && showOptimalPath) {
      return { color: COLORS.edgeOptimalPath, width: 4, priority: 2 };
    } else if (isInSelectedPath && isSelectedConsecutive) {
      return { color: COLORS.edgePath, width: 4, priority: 1 };
    } else {
      return { color: COLORS.edge, width: 3, priority: 0 };
    }
  };

  // Mouse down = start dragging if clicked on a node
  const handleMouseDown = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const node = findNodeAtPosition(x, y);
    if (node) {
      setDraggedNode(node);
    }
  };

  // Mouse move = update dragged node position
  const handleMouseDrag = (e) => {
    if (!draggedNode) {
      // still do hover detection
      handleMouseMove(e);
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const transform = calculateFitTransform();
    // Reverse transform to get back to graph coordinate system
    const newX = (x - transform.offsetX) / transform.scale;
    const newY = (y - transform.offsetY) / transform.scale;

    draggedNode.x = newX;
    draggedNode.y = newY;

    drawGraph(); // redraw in real-time
  };

  // Mouse up = stop dragging
  const handleMouseUp = () => {
    setDraggedNode(null);
  };

  // Draw the graph
  const drawGraph = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Enable anti-aliasing for smoother rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Create subtle background gradient
    const bgGradient = ctx.createLinearGradient(0, 0, width, height);
    bgGradient.addColorStop(0, '#fafafa');
    bgGradient.addColorStop(1, '#f5f5f5');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width, height);
    
    const transform = calculateFitTransform();

    // Draw edges
    edges.forEach(edge => {
      const fromNode = nodes.find(n => n.id === edge.from_);
      const toNode = nodes.find(n => n.id === edge.to);
      
      if (fromNode && toNode) {
        const fromPos = transformCoord(fromNode.x, fromNode.y, transform);
        const toPos = transformCoord(toNode.x, toNode.y, transform);
        
        // Get edge styling based on path types
        const edgeStyle = getEdgeStyle(fromNode.id, toNode.id);
        
        // Edge styling
        ctx.strokeStyle = edgeStyle.color;
        ctx.lineWidth = edgeStyle.width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Add subtle shadow for edges
        ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        
        // Smooth edge drawing
        ctx.beginPath();
        ctx.moveTo(fromPos.x, fromPos.y);
        ctx.lineTo(toPos.x, toPos.y);
        ctx.stroke();
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Draw edge weight
        const midX = (fromPos.x + toPos.x) / 2;
        const midY = (fromPos.y + toPos.y) / 2;
        
        ctx.fillStyle = '#8B5CF6'; // Purple color for path costs
        ctx.font = `bold ${14 * transform.scale}px Arial`; // Bold font for path costs
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(edge.length.toString(), midX, midY);
      }
    });

    // Draw nodes
    nodes.forEach(node => {
      const pos = transformCoord(node.x, node.y, transform);
      const scaledRadius = NODE_RADIUS * transform.scale;
      
      const isSelected = selectedPath.includes(node.id);
      const isHovered = hoveredNode === node.id;
      const isCurrent = stepData && stepData.current_node === node.id;
      const isVisited = stepData && stepData.visited && stepData.visited.includes(node.id);
      
      // Node color logic (lab-style)
      let color = COLORS.node;
      let borderColor = COLORS.border;
      
      if (isCurrent) {
        color = COLORS.nodeCurrent;
        borderColor = '#e53e3e';
      } else if (isVisited) {
        color = COLORS.nodeVisited;
        borderColor = '#38a169';
      } else if (isSelected) {
        color = COLORS.nodeSelected;
        borderColor = '#FFA500';
      } else if (isHovered) {
        color = COLORS.nodeHover;
        borderColor = '#2B7CE9';
      }

      // Draw node shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 3;

      // Create gradient for node
      const gradient = ctx.createRadialGradient(
        pos.x - scaledRadius * 0.3, pos.y - scaledRadius * 0.3, 0,
        pos.x, pos.y, scaledRadius
      );
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, color === COLORS.node ? '#f0f0f0' : color);

      // Node circle with gradient
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, scaledRadius, 0, 2 * Math.PI);
      ctx.fill();

      // Reset shadow for border
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Node border with smoother appearance
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 2.5 * transform.scale;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Node label
      ctx.fillStyle = COLORS.text;
      ctx.font = `bold ${20 * transform.scale}px Arial`; // Increased from 16 to 20
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(node.label, pos.x, pos.y);

      // Draw distance if available in step data
      if (stepData && stepData.distances && stepData.distances[node.id] !== undefined) {
        const distance = stepData.distances[node.id];
        const distanceText = distance === Infinity ? '∞' : distance.toString();
        
        ctx.fillStyle = COLORS.text;
        ctx.font = `${14 * transform.scale}px Arial`; // Increased from 12 to 14
        ctx.textAlign = 'center';
        ctx.fillText(distanceText, pos.x, pos.y + scaledRadius + 20 * transform.scale);
      }
    });
  };

  // Event handlers
  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const node = findNodeAtPosition(x, y);
    setHoveredNode(node ? node.id : null);
  };

  const handleClick = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const node = findNodeAtPosition(x, y);
    if (node && onNodeClick) {
      onNodeClick(node);
    }
  };

  // Redraw when data changes
  useEffect(() => {
    drawGraph();
  }, [nodes, edges, selectedPath, hoveredNode, stepData, customPath, optimalPath, showOptimalPath]);

  return (
    <div className="graph-visualizer">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseDrag}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        style={{ 
          cursor: hoveredNode ? 'pointer' : 'default',
          background: '#f9f9f9',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      />
    </div>
  );
};

export default GraphVisualizer;
